'use client';

import { useState } from 'react';
import { cn } from '../../lib/utils';

export function JSONFormatter() {
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const formatJSON = () => {
    try {
      const parsed = JSON.parse(input);
      const formatted = JSON.stringify(parsed, null, 2);
      setOutput(formatted);
      setError('');
    } catch (err) {
      setError(`Fehler: ${err.message}`);
      setOutput('');
    }
  };

  const minifyJSON = () => {
    try {
      const parsed = JSON.parse(input);
      const minified = JSON.stringify(parsed);
      setOutput(minified);
      setError('');
    } catch (err) {
      setError(`Fehler: ${err.message}`);
      setOutput('');
    }
  };

  const validateJSON = () => {
    try {
      JSON.parse(input);
      setError('');
      setOutput('✓ Valides JSON!');
    } catch (err) {
      setError(`Fehler: ${err.message}`);
      setOutput('');
    }
  };

  const copyToClipboard = async () => {
    if (!output) return;

    try {
      await navigator.clipboard.writeText(output);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const clearAll = () => {
    setInput('');
    setOutput('');
    setError('');
  };

  const loadExample = () => {
    const example = {
      "name": "Beispiel Daten",
      "version": "1.0.0",
      "features": ["Format", "Minify", "Validate"],
      "nested": {
        "property": "Wert",
        "array": [1, 2, 3, 4, 5]
      }
    };
    setInput(JSON.stringify(example, null, 2));
    setOutput('');
    setError('');
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-8">
      <h3 className="font-heading font-bold text-2xl mb-4">
        JSON Formatter & Validator
      </h3>
      <p className="text-gray-700 mb-6">
        Formatiere, minifiziere und validiere JSON direkt im Browser. Alle Daten bleiben lokal.
      </p>

      {/* Input Section */}
      <div className="space-y-4 mb-6">
        <div>
          <div className="flex items-center justify-between mb-2">
            <label htmlFor="json-input" className="block text-sm font-semibold">
              JSON Eingabe
            </label>
            <button
              onClick={loadExample}
              className="text-sm text-accent hover:text-accent-dark font-semibold"
            >
              Beispiel laden
            </button>
          </div>
          <textarea
            id="json-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder='{"key": "value"}'
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent font-mono text-sm min-h-[200px]"
          />
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3">
          <button
            onClick={formatJSON}
            className="bg-accent hover:bg-accent-dark text-white font-semibold px-6 py-3 rounded-lg transition-colors"
          >
            Formatieren
          </button>
          <button
            onClick={minifyJSON}
            className="bg-primary hover:bg-primary-dark text-white font-semibold px-6 py-3 rounded-lg transition-colors"
          >
            Minify
          </button>
          <button
            onClick={validateJSON}
            className="bg-green-600 hover:bg-green-700 text-white font-semibold px-6 py-3 rounded-lg transition-colors"
          >
            Validieren
          </button>
          <button
            onClick={clearAll}
            className="bg-gray-500 hover:bg-gray-600 text-white font-semibold px-6 py-3 rounded-lg transition-colors"
          >
            Leeren
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-red-800">
            <strong>⚠️ Validierungsfehler:</strong> {error}
          </p>
        </div>
      )}

      {/* Output Section */}
      {output && (
        <div className="space-y-4">
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-semibold">
                Ergebnis
              </label>
              <button
                onClick={copyToClipboard}
                className={cn(
                  'px-4 py-2 rounded-lg text-sm font-semibold transition-colors',
                  copied
                    ? 'bg-green-500 text-white'
                    : 'bg-accent hover:bg-accent-dark text-white'
                )}
              >
                {copied ? '✓ Kopiert!' : 'Kopieren'}
              </button>
            </div>
            <div className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto">
              <pre className="text-sm font-mono whitespace-pre-wrap break-all">
                {output}
              </pre>
            </div>
          </div>

          {/* Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>ℹ️ Hinweis:</strong> Alle Daten werden nur lokal in deinem Browser verarbeitet.
              Nichts wird an einen Server gesendet.
            </p>
          </div>
        </div>
      )}

      {!output && !error && (
        <div className="text-center text-gray-500 py-8">
          Füge JSON ein und wähle eine Aktion
        </div>
      )}

      {/* Feature List */}
      <div className="mt-8 bg-gray-50 rounded-lg p-6">
        <h4 className="font-semibold text-lg mb-4">Features</h4>
        <ul className="space-y-2">
          <li className="flex items-start gap-2 text-sm">
            <span className="text-accent mt-1">✓</span>
            <span className="text-gray-700">
              <strong>Formatieren:</strong> Wandelt kompaktes JSON in lesbares Format mit Einrückungen
            </span>
          </li>
          <li className="flex items-start gap-2 text-sm">
            <span className="text-accent mt-1">✓</span>
            <span className="text-gray-700">
              <strong>Minify:</strong> Entfernt alle unnötigen Leerzeichen für minimale Dateigröße
            </span>
          </li>
          <li className="flex items-start gap-2 text-sm">
            <span className="text-accent mt-1">✓</span>
            <span className="text-gray-700">
              <strong>Validieren:</strong> Prüft JSON auf Syntax-Fehler mit detaillierter Fehlermeldung
            </span>
          </li>
          <li className="flex items-start gap-2 text-sm">
            <span className="text-accent mt-1">✓</span>
            <span className="text-gray-700">
              <strong>100% Client-Side:</strong> Deine Daten verlassen niemals deinen Browser
            </span>
          </li>
        </ul>
      </div>
    </div>
  );
}
